<template>
  <div>
    <DataTable
      ref="datatableRef"
      :config="datatableConfig"
      :data="decoratedItems"
      :refreshButton="refreshButtonConfig"
      :data-testid="`pdv-laudos-${type}-table`"
      @refresh="loadData"
      @button-click="handleButtonClick"
    />

    <modal
      :elem-id="`modal_pdv_laudos_${type}_report`"
      :show="reportModalVisible"
      :title="reportModalTitle"
      size="large"
      :saveLabel="saveLabel"
      :saveIcon="'pi pi-check'"
      :saveFn="saveFn"
      :saveDisabledFn="saveDisabledFn"
      :saveTestId="saveTestId"
      @close="reportModalVisible = false"
      @dismiss="reportModalVisible = false"
    >
      <component :is="reportComponent" ref="reportRef" v-if="reportModalVisible && selectedOrderItemId !== undefined" :orderItemId="selectedOrderItemId" embedded :editable-by-default="true" />
    </modal>

    <modal
      :elem-id="`modal_pdv_laudos_${type}_checkout`"
      :show="checkoutModalVisible"
      :title="t('Ordem de Serviço')"
      size="xlarge"
      @close="checkoutModalVisible = false"
      @dismiss="checkoutModalVisible = false"
    >
      <PDVCheckoutForm v-if="checkoutModalVisible && checkoutOrderId !== undefined && checkoutOrderId !== null" :order-id="checkoutOrderId" host="page" />
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useSOLaudosPending } from '../../composables/useSOLaudosPending';
import { log, error as logError } from '../../utils/DebugLogger';
import DataTable from '../../grid/DataTable.vue';
import SOEntryReportPage from '../so-orders/SOEntryReportPage.vue';
import PDVExitReportPage from './PDVExitReportPage.vue';
import PDVCheckoutForm from './PDVCheckoutForm.vue';
import type { DataTableColumn, DataTableConfig } from '../../grid/types';
import type { Orders_PendingLaudoItemRow } from '../../contracts/Orders_PendingLaudoItemRow';

type Id = number | string;
type LaudoType = 'entrada' | 'saida';

interface ReportRefInstance {
  handleConfirm?: () => void | Promise<void>;
  canConfirm?: boolean;
  isSaving?: boolean;
  isConfirming?: boolean;
}

type DecoratedRow = Orders_PendingLaudoItemRow & {
  _so_order_label: string;
  _so_order_item_label: string;
  _service_with_priority: string;
};

const props = defineProps<{
  type: LaudoType;
}>();

const { t } = useI18n();
const datatableRef = ref<{ loadData?: () => void } | null>(null);
const { items: rawItems, fetchPendingItems } = useSOLaudosPending(props.type);

const reportModalVisible = ref<boolean>(false);
const reportRef = ref<ReportRefInstance | null>(null);
const selectedOrderItemId = ref<Id | null>(null);
const checkoutModalVisible = ref<boolean>(false);
const checkoutOrderId = ref<Id | null>(null);

const decoratedItems = computed<DecoratedRow[]>(() =>
  rawItems.value.map((r) => ({
    ...r,
    _so_order_label: `Ordem ID: ${r.so_order_id}`,
    _so_order_item_label: `Item ID: ${r.so_order_item_id}`,
    _service_with_priority: r.so_priority_name
      ? `${r.so_service_name ?? ''} ${r.so_priority_name}`.trim()
      : (r.so_service_name ?? ''),
  }))
);

const isReportSaving = computed<boolean>(() =>
  props.type === 'saida' ? !!reportRef.value?.isSaving : !!reportRef.value?.isConfirming
);

const saveLabel = computed<string>(() => {
  if (props.type === 'saida') {
    return isReportSaving.value ? t('Confirmando...') : t('Concluir e Notificar Cliente');
  }
  return isReportSaving.value ? t('Confirmando...') : t('Confirmar Laudo');
});

const saveFn = (): void | Promise<void> => reportRef.value?.handleConfirm?.();

const saveDisabledFn = (): boolean => {
  const refVal = reportRef.value;
  const canConfirm = refVal?.canConfirm;
  const saving = isReportSaving.value;
  return !canConfirm || saving;
};

const saveTestId = computed<string>(() =>
  props.type === 'saida' ? 'exit-report-confirm-btn' : 'entry-report-confirm-btn'
);

const reportComponent = computed(() => (
  props.type === 'entrada' ? SOEntryReportPage : PDVExitReportPage
));

const reportModalTitle = computed<string>(() => (
  props.type === 'entrada' ? t('Laudo de Entrada') : t('Laudo de Saída')
));

const tableColumns = computed<DataTableColumn[]>(() => {
  const columns: DataTableColumn[] = [
    {
      data: '_so_order_label',
      sortField: 'so_order_id',
      title: t('OS'),
      width: 110,
      sortable: true,
      renderType: 'custom_clickable_id',
      clickCallback: (_value: unknown, row?: Record<string, unknown>) => {
        const orderId = row?.so_order_id as Id | undefined;
        if (orderId !== undefined) {
          checkoutOrderId.value = orderId;
          checkoutModalVisible.value = true;
        }
      },
      mergedColumns: [
        {
          data: '_so_order_item_label',
          title: t('Item'),
          renderType: 'custom_clickable_id',
          clickCallback: (_value: unknown, row?: Record<string, unknown>) => {
            const itemId = row?.so_order_item_id as Id | undefined;
            if (itemId) {
              eventBus.emit('modal.so-order-item.open', { itemId });
            }
          },
        },
      ],
    } as DataTableColumn,
    {
      data: null,
      title: t('Ações'),
      width: 140,
      sortable: false,
      filterable: false,
      renderType: 'clickable_button',
      buttonText: t('Gerar Laudo'),
      buttonIcon: 'pi pi-file-edit',
      buttonClass: '',
      buttonAction: 'gerar-laudo',
      buttonTestId: 'pdv-laudos-gerar-laudo-button',
    } as DataTableColumn,
  ];

  columns.push({
    data: 'so_order_item_qrcode',
    title: t('Código QR'),
    width: 180,
    sortable: true,
    filterType: 'qrcode',
  } as DataTableColumn);

  columns.push(
    {
      data: 'so_order_client_name',
      title: t('Cliente'),
      width: 220,
      sortable: true,
      renderType: 'custom_clickable_id',
      clickCallback: (_value: unknown, row?: Record<string, unknown>) => {
        const clienteId = row?.so_order_client_id as Id | undefined;
        if (clienteId) {
          eventBus.emit('modal.cliente.open', { id: clienteId });
        }
      },
      mergedColumns: [
        { data: 'franchise_name', title: t('Franquia') },
      ],
    } as DataTableColumn,
    {
      data: '_service_with_priority',
      title: t('Serviço'),
      width: 220,
      sortable: true,
      mergedColumns: [
        { data: 'so_service_category_name', title: t('Categoria') },
        { data: 'item_description_value', title: t('Descrição') },
      ],
    } as DataTableColumn,
    {
      data: 'so_order_created_at',
      title: t('Criado em'),
      width: 170,
      sortable: true,
      renderType: 'datetime',
    } as DataTableColumn
  );

  return columns;
});

const datatableConfig = computed<DataTableConfig>(() => ({
  tableId: props.type === 'entrada' ? 'pdvLaudosEntradaDataTable' : 'pdvLaudosSaidaDataTable',
  rowTestId: (row: Record<string, unknown>) => {
    const id = row.so_order_item_id;
    return id == null ? undefined : `pdv-laudos-row-${id}`;
  },
  filename: `pdv_laudos_${props.type}_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  // Both entrada and saida queues sort newest-first by item id so freshly
  // confirmed items appear at the top of page 1 (matches the backend SQL
  // ORDER BY so_order_item_id DESC).
  defaultSortField: 'so_order_item_id',
  defaultSortOrder: -1,
  pageLength: 25,
  heightMode: 'fill-viewport',
  pagination: true,
  rowSelection: 'single',
  sortable: true,
  resizableColumns: true,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small',
}));

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar'),
}));

const loadData = async (): Promise<void> => {
  log('PDVLaudosPendingPage', 'loadData', `Loading ${props.type} items`);
  try {
    await fetchPendingItems();
  } catch (err) {
    logError('PDVLaudosPendingPage', 'loadData', `Error loading ${props.type} items`, err);
    eventBus.emit(
      'globalMessage.error',
      props.type === 'entrada'
        ? t('Erro ao carregar itens pendentes de laudo de entrada')
        : t('Erro ao carregar itens pendentes de laudo de saída')
    );
  }
};

const openReport = (orderItemId: Id): void => {
  if (!orderItemId) return;
  selectedOrderItemId.value = orderItemId;
  reportModalVisible.value = true;
};

const handleButtonClick = (payload: { data?: Record<string, unknown>; action?: string }): void => {
  if (payload?.action !== 'gerar-laudo') return;
  const itemId = payload?.data?.so_order_item_id as Id | undefined;
  if (itemId !== undefined) {
    openReport(itemId);
  }
};

const handleModalClose = (): void => {
  reportModalVisible.value = false;
};

onMounted(() => {
  loadData();
  eventBus.on('so-orders.saved', loadData);
  eventBus.on('filter.updated', loadData);
  eventBus.on('modal.pdv-laudos-entrada.close', handleModalClose);
  eventBus.on('modal.pdv-laudos-saida.close', handleModalClose);
});

onBeforeUnmount(() => {
  eventBus.off('so-orders.saved', loadData);
  eventBus.off('filter.updated', loadData);
  eventBus.off('modal.pdv-laudos-entrada.close', handleModalClose);
  eventBus.off('modal.pdv-laudos-saida.close', handleModalClose);
});

defineExpose({ loadData, datatableRef });
</script>
